import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Alert,
  Checkbox,
} from '@mui/material';
import { Save, Add, AutoFixHigh, Science, DeleteSweep, Search as SearchIcon } from '@mui/icons-material';
import { dhis2ConfigAPI, type Dhis2ConfigOut } from '../../services/api';

interface LabRow {
  observation_key: string;
  aliases: string;
  section_id: string;
  field_id: string;
}

const LAB_MAPPINGS_KEY = 'lab_observations';

/** Schema section/field for dropdowns (supports groups per parent-child implementation) */
interface SchemaSection {
  id: string;
  name: string;
  fields?: Array<{ id: string; name?: string; type?: string; description?: string }>;
  groups?: Array<{ id: string; name: string; fields?: Array<{ id: string; name?: string; type?: string; description?: string }> }>;
}

interface SchemaTarget {
  section_id: string;
  section_name: string;
  field_id: string;
  field_name: string;
}

/** Lab-like keywords for "Suggest from schema" (field name/description) */
const LAB_LIKE_KEYWORDS = [
  'glucose', 'hba1c', 'hemoglobin', 'haemoglobin', 'hb', 'hgb', 'cbc', 'creatinine',
  'cholesterol', 'rbc', 'wbc', 'platelet', 'blood pressure', 'weight', 'bmi', 'height',
  'systolic', 'diastolic', 'neutrophil', 'lymphocyte', 'eosinophil', 'basophil', 'monocyte',
  'alt', 'ast', 'bilirubin', 'urea', 'sugar', 'fasting', 'pp', 'post prandial', 'a1c',
  'glycated', 'rbs', 'fbs', 'ppbs', 'lipid', 'hdl', 'ldl', 'triglyceride', 'vitamin',
  'tsh', 't3', 't4', 'creatinine', 'egfr', 'albumin', 'uric', 'calcium', 'sodium', 'potassium',
];

/** Common labs for "Add from common labs" (observation_key, aliases) */
const COMMON_LABS: Array<{ observation_key: string; aliases: string }> = [
  { observation_key: 'hba1c', aliases: 'HbA1c, Hemoglobin A1c, A1c, Glycated hemoglobin' },
  { observation_key: 'fasting_glucose', aliases: 'FBS, Fasting blood sugar, Fasting glucose' },
  { observation_key: 'post_prandial_glucose', aliases: 'PPBS, Post-prandial glucose, 2hr glucose' },
  { observation_key: 'haemoglobin', aliases: 'Hb, Hemoglobin, Hgb, Haemoglobin (g/dL)' },
  { observation_key: 'rbc', aliases: 'RBC, Red blood cells, Erythrocytes' },
  { observation_key: 'wbc', aliases: 'WBC, White blood cells, Leukocytes' },
  { observation_key: 'platelets', aliases: 'Platelets, PLT, Thrombocytes' },
  { observation_key: 'creatinine', aliases: 'Creatinine, Serum creatinine' },
  { observation_key: 'urea', aliases: 'Urea, BUN, Blood urea nitrogen' },
  { observation_key: 'cholesterol_total', aliases: 'Total cholesterol, Cholesterol' },
  { observation_key: 'hdl', aliases: 'HDL, HDL cholesterol' },
  { observation_key: 'ldl', aliases: 'LDL, LDL cholesterol' },
  { observation_key: 'triglycerides', aliases: 'Triglycerides, TG' },
  { observation_key: 'weight', aliases: 'Weight, Body weight (kg)' },
  { observation_key: 'height', aliases: 'Height, Stature (cm)' },
  { observation_key: 'bmi', aliases: 'BMI, Body mass index' },
  { observation_key: 'blood_pressure_systolic', aliases: 'Systolic BP, SBP, Blood pressure systolic' },
  { observation_key: 'blood_pressure_diastolic', aliases: 'Diastolic BP, DBP, Blood pressure diastolic' },
];

function normalizeForKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .replace(/_+/g, '_') || name.toLowerCase().replace(/\s+/g, '_');
}

interface LabMapperEditorProps {
  configs: Dhis2ConfigOut[];
  selectedConfigId: string | null;
  onSelectConfigId: (id: string | null) => void;
}

function matchLabSearch(row: LabRow, q: string): boolean {
  if (!q.trim()) return true;
  const lower = q.toLowerCase().trim();
  return [row.observation_key, row.aliases, row.section_id, row.field_id].some((s) => s && s.toLowerCase().includes(lower));
}

export default function LabMapperEditor({ configs, selectedConfigId, onSelectConfigId }: LabMapperEditorProps) {
  const [rows, setRows] = useState<LabRow[]>([]);
  const [schema, setSchema] = useState<{ sections?: SchemaSection[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const selectedConfig = configs.find((c) => c.id === selectedConfigId) ?? null;
  const isWebAdapterConfig = (selectedConfig?.schema_source ?? '').toLowerCase() === 'admin_defined';

  const schemaTargets: SchemaTarget[] = useMemo(() => {
    const sections = schema?.sections ?? [];
    const out: SchemaTarget[] = [];
    for (const sec of sections) {
      const sectionId = sec.id ?? '';
      const sectionName = sec.name ?? sectionId;
      for (const f of sec.fields ?? []) {
        const fieldId = f.id ?? f.name ?? '';
        if (!fieldId) continue;
        out.push({ section_id: sectionId, section_name: sectionName, field_id: fieldId, field_name: f.name ?? fieldId });
      }
      for (const grp of sec.groups ?? []) {
        for (const f of grp.fields ?? []) {
          const fieldId = f.id ?? f.name ?? '';
          if (!fieldId) continue;
          out.push({ section_id: sectionId, section_name: sectionName, field_id: fieldId, field_name: f.name ?? fieldId });
        }
      }
    }
    return out;
  }, [schema]);

  const sectionsList = useMemo(() => {
    const seen = new Set<string>();
    const list: { id: string; name: string }[] = [];
    for (const s of schema?.sections ?? []) {
      const id = s.id ?? '';
      if (id && !seen.has(id)) {
        seen.add(id);
        list.push({ id, name: s.name ?? id });
      }
    }
    return list;
  }, [schema]);

  const fieldsBySection = useMemo(() => {
    const map: Record<string, Array<{ id: string; name: string }>> = {};
    for (const t of schemaTargets) {
      if (!map[t.section_id]) map[t.section_id] = [];
      map[t.section_id].push({ id: t.field_id, name: t.field_name });
    }
    return map;
  }, [schemaTargets]);

  const filteredRowsWithIndex = useMemo(
    () => rows.map((row, index) => ({ row, index })).filter(({ row }) => matchLabSearch(row, searchText)),
    [rows, searchText]
  );

  const handleToggleSelect = (index: number) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const visibleIndices = useMemo(() => new Set(filteredRowsWithIndex.map(({ index }) => index)), [filteredRowsWithIndex]);
  const allVisibleSelected = visibleIndices.size > 0 && filteredRowsWithIndex.every(({ index }) => selectedIndices.has(index));
  const someVisibleSelected = filteredRowsWithIndex.some(({ index }) => selectedIndices.has(index));

  const handleSelectAll = (checked: boolean) => {
    if (checked) setSelectedIndices(new Set(visibleIndices));
    else setSelectedIndices((prev) => new Set([...prev].filter((i) => !visibleIndices.has(i))));
  };

  const handleBulkRemove = () => {
    if (selectedIndices.size === 0) return;
    setRows((prev) => prev.filter((_, i) => !selectedIndices.has(i)));
    setSelectedIndices(new Set());
  };

  const loadMappingsAndSchema = async () => {
    if (!selectedConfigId) {
      setRows([]);
      setSchema(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [mapRes, schemaRes] = await Promise.all([
        dhis2ConfigAPI.getFieldMappings(selectedConfigId),
        dhis2ConfigAPI.getSchema(selectedConfigId),
      ]);
      const data = (mapRes.data ?? {}) as Record<string, unknown>;
      const list = (data[LAB_MAPPINGS_KEY] ?? data) as Array<Record<string, string>> | undefined;
      const arr = Array.isArray(list)
        ? list.map((r) => ({
            observation_key: r.observation_key ?? r.observationKey ?? '',
            aliases: typeof r.aliases === 'string' ? r.aliases : (Array.isArray(r.aliases) ? (r.aliases as string[]).join(', ') : ''),
            section_id: r.section_id ?? r.sectionId ?? '',
            field_id: r.field_id ?? r.fieldId ?? '',
          }))
        : [];
      setRows(arr);
      setSelectedIndices(new Set());
      setSchema((schemaRes.data ?? {}) as { sections?: SchemaSection[] });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load lab mappings');
      setRows([]);
      setSchema(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMappingsAndSchema();
  }, [selectedConfigId]);

  const handleSave = async () => {
    if (!selectedConfigId) return;
    setSaving(true);
    setError(null);
    try {
      const res = await dhis2ConfigAPI.getFieldMappings(selectedConfigId);
      const current = (res.data ?? {}) as Record<string, unknown>;
      const updated = { ...current, [LAB_MAPPINGS_KEY]: rows };
      await dhis2ConfigAPI.putFieldMappings(selectedConfigId, updated);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const updateRow = (index: number, field: keyof LabRow, value: string) => {
    setRows((prev) => {
      const next = [...prev];
      if (next[index]) {
        next[index] = { ...next[index], [field]: value };
        if ((field === 'section_id') && value) {
          next[index] = { ...next[index], field_id: '' };
        }
      }
      return next;
    });
  };

  const addRow = () => {
    setRows((prev) => [...prev, { observation_key: '', aliases: '', section_id: '', field_id: '' }]);
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const suggestFromSchema = () => {
    const sections = schema?.sections ?? [];
    const newRows: LabRow[] = [];
    const addField = (sectionId: string, f: { id: string; name?: string; description?: string }) => {
      const fieldId = f.id ?? f.name ?? '';
      const fieldName = (f.name ?? fieldId).toLowerCase();
      const desc = ((f.description ?? '') as string).toLowerCase();
      const combined = `${fieldName} ${desc}`;
      const isLabLike = LAB_LIKE_KEYWORDS.some((kw) => combined.includes(kw));
      if (!isLabLike) return;
      newRows.push({
        observation_key: normalizeForKey(f.name ?? fieldId),
        aliases: f.name ?? fieldId ?? '',
        section_id: sectionId,
        field_id: fieldId,
      });
    };
    for (const sec of sections) {
      const sectionId = sec.id ?? '';
      for (const f of sec.fields ?? []) addField(sectionId, f);
      for (const grp of sec.groups ?? []) {
        for (const f of grp.fields ?? []) addField(sectionId, f);
      }
    }
    setRows((prev) => [...prev, ...newRows]);
  };

  const addFromCommonLabs = () => {
    const newRows: LabRow[] = COMMON_LABS.map((c) => ({
      observation_key: c.observation_key,
      aliases: c.aliases,
      section_id: '',
      field_id: '',
    }));
    setRows((prev) => [...prev, ...newRows]);
  };

  const loadWebDefaults = async () => {
    setError(null);
    try {
      const tpl = await dhis2ConfigAPI.getWebOpdDefaultTemplate();
      const mappings = (tpl.data?.field_mappings_json ?? {}) as Record<string, unknown>;
      const list = (mappings[LAB_MAPPINGS_KEY] ?? []) as Array<Record<string, string>>;
      const arr = Array.isArray(list)
        ? list.map((r) => ({
            observation_key: r.observation_key ?? r.observationKey ?? '',
            aliases: typeof r.aliases === 'string' ? r.aliases : (Array.isArray(r.aliases) ? (r.aliases as string[]).join(', ') : ''),
            section_id: r.section_id ?? r.sectionId ?? '',
            field_id: r.field_id ?? r.fieldId ?? '',
          }))
        : [];
      setRows(arr);
      setSelectedIndices(new Set());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load web adapter lab defaults');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Config</InputLabel>
          <Select
            value={selectedConfigId ?? ''}
            label="Config"
            onChange={(e) => onSelectConfigId(e.target.value || null)}
          >
            <MenuItem value="">— Select config —</MenuItem>
            {configs.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={!selectedConfigId || saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="outlined" startIcon={<Add />} onClick={addRow} disabled={!selectedConfigId}>
          Add row
        </Button>
        <Button
          variant="outlined"
          startIcon={<AutoFixHigh />}
          onClick={suggestFromSchema}
          disabled={!selectedConfigId || !schema?.sections?.length}
        >
          Suggest from schema
        </Button>
        <Button
          variant="outlined"
          startIcon={<Science />}
          onClick={addFromCommonLabs}
          disabled={!selectedConfigId}
        >
          Add from common labs
        </Button>
        <Button
          variant="outlined"
          onClick={loadWebDefaults}
          disabled={!selectedConfigId || !isWebAdapterConfig}
        >
          Load web adapter defaults
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteSweep />}
          onClick={handleBulkRemove}
          disabled={!selectedConfigId || selectedIndices.size === 0}
        >
          Remove selected ({selectedIndices.size})
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!selectedConfigId ? (
        <Typography color="text.secondary">Select a config to edit lab observation mappings.</Typography>
      ) : loading ? (
        <Typography>Loading…</Typography>
      ) : (
        <Box>
          <TextField
            size="small"
            placeholder="Search by observation key, aliases, section, or field…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} /> }}
            sx={{ mb: 1.5, maxWidth: 400 }}
            fullWidth
          />
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={someVisibleSelected && !allVisibleSelected}
                      checked={allVisibleSelected}
                      onChange={(_, checked) => handleSelectAll(checked)}
                      title="Select all visible for removal"
                    />
                  </TableCell>
                  <TableCell>Observation key</TableCell>
                  <TableCell>Aliases</TableCell>
                  <TableCell>Section</TableCell>
                  <TableCell>Field</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRowsWithIndex.map(({ row: r, index: i }) => (
                  <TableRow key={i} selected={selectedIndices.has(i)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIndices.has(i)}
                        onChange={() => handleToggleSelect(i)}
                        onClick={(e) => e.stopPropagation()}
                        title="Select for removal"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={r.observation_key}
                        onChange={(e) => updateRow(i, 'observation_key', e.target.value)}
                        placeholder="e.g. haemoglobin (g/dl)"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={r.aliases}
                        onChange={(e) => updateRow(i, 'aliases', e.target.value)}
                        placeholder="Hb, Hemoglobin, Hgb"
                      />
                    </TableCell>
                    <TableCell>
                      {sectionsList.length > 0 ? (
                        <FormControl size="small" fullWidth>
                          <InputLabel>Section</InputLabel>
                          <Select value={r.section_id || ''} label="Section" onChange={(e) => updateRow(i, 'section_id', e.target.value)}>
                            <MenuItem value="">— Select —</MenuItem>
                            {sectionsList.map((s) => (
                              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField size="small" fullWidth value={r.section_id} onChange={(e) => updateRow(i, 'section_id', e.target.value)} placeholder="section_id" />
                      )}
                    </TableCell>
                    <TableCell>
                      {r.section_id && fieldsBySection[r.section_id] ? (
                        <FormControl size="small" fullWidth>
                          <InputLabel>Field</InputLabel>
                          <Select value={r.field_id || ''} label="Field" onChange={(e) => updateRow(i, 'field_id', e.target.value)}>
                            <MenuItem value="">— Select —</MenuItem>
                            {fieldsBySection[r.section_id].map((f) => (
                              <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField size="small" fullWidth value={r.field_id} onChange={(e) => updateRow(i, 'field_id', e.target.value)} placeholder="field_id" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" color="error" onClick={() => removeRow(i)}>Remove</Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredRowsWithIndex.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      {rows.length === 0
                        ? 'No rows. Use "Add row", "Suggest from schema", or "Add from common labs", then assign Section/Field from dropdowns.'
                        : 'No rows match the search.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}
